import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  File,
  MapPin,
  MessageSquare,
  Shield,
  ImageIcon,
  AlertTriangle,
  Upload,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';

interface OrderDetail {
  id: number;
  service_id: number;
  service_name?: string;
  property_id?: number;
  property_name?: string;
  postcode?: string;
  total_amount_pence: number;
  status: string;
  payment_status?: string;
  is_paid?: number;
  payment_completed_at?: string;
  created_at: string;
  updated_at: string;
  booking_id?: number;
  booking_status?: string;
  accessInstructions?: string;
  description?: string;
  preferred_service_date?: string;
  preferred_service_time?: string;
  [key: string]: any;
}

const OrderDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [photos, setPhotos] = useState({ before: [], during: [], evidence: [] });
  const [docsLoading, setDocsLoading] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [photosLoading, setPhotosLoading] = useState(false);

  // Fetch order data from API
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // Try to fetch as a booking/order
        const response = await apiService.request(`/customer/orders`, 'GET');
        if (response.success && Array.isArray(response.data)) {
          const foundOrder = response.data.find((o: OrderDetail) => o.id?.toString() === id);
          if (foundOrder) {
            setOrder(foundOrder);
            setError(null);
          } else {
            setError('Order not found');
          }
        } else {
          setError('Failed to load order');
        }
      } catch (err) {
        console.error('Failed to fetch order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [id]);

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!id) return;
      setDocsLoading(true);
      try {
        const response = await apiService.request(`/orders/${id}/documents`, 'GET');
        if (response.success && Array.isArray(response.data)) {
          setDocuments(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch documents:', err);
      } finally {
        setDocsLoading(false);
      }
    };

    fetchDocuments();
  }, [id]);

  // Fetch timeline from API
  useEffect(() => {
    const fetchTimeline = async () => {
      if (!id) return;
      setTimelineLoading(true);
      try {
        const response = await apiService.request(`/orders/${id}/timeline`, 'GET');
        if (response.success && Array.isArray(response.data)) {
          setTimeline(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch timeline:', err);
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [id]);

  // Fetch photos from API
  useEffect(() => {
    const fetchPhotos = async () => {
      if (!id) return;
      setPhotosLoading(true);
      try {
        const response = await apiService.request(`/orders/${id}/photos`, 'GET');
        if (response.success && response.data) {
          setPhotos(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch photos:', err);
      } finally {
        setPhotosLoading(false);
      }
    };

    fetchPhotos();
  }, [id]);

  // Helper function to normalize tab names
  const normalizeTabName = (tab: string) => {
    return tab
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/&/g, '')
      .replace(/-+/g, '-');
  };

  // Handle URL hash for tab navigation
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const normalizedHash = hash.toLowerCase();
      setActiveTab(normalizedHash);
    }
  }, []);

  // Update URL when activeTab changes
  useEffect(() => {
    window.history.replaceState(null, '', `#${activeTab}`);
  }, [activeTab]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !order) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto py-12 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The order you are looking for does not exist.'}</p>
          <Button onClick={() => navigate('/active-orders')} className="w-full">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Active Orders
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const amountGBP = ((order?.total_amount_pence ?? 0) / 100).toFixed(2);
  const orderNumber = `#ORD-${order?.id}`;

  const handleDownloadPDF = () => {
    if (!order) return;
    const doc = new jsPDF();
    const green = [0, 84, 59] as [number, number, number];
    const gray = [100, 100, 100] as [number, number, number];

    // Header bar
    doc.setFillColor(...green);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('VIREWIRE', 14, 14);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Quotation / Order Summary', 100, 14, { align: 'center' });

    // Reference + Date
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Order ${orderNumber}`, 14, 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    doc.text(`Date: ${new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}`, 14, 41);
    doc.text(`Status: ${(order.booking_status || order.status || 'Pending').toUpperCase()}`, 14, 47);

    // Divider
    doc.setDrawColor(...green);
    doc.setLineWidth(0.5);
    doc.line(14, 53, 196, 53);

    // Service details
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Service Details', 14, 62);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    const details: [string, string][] = [
      ['Service', order.service_name || order.sr_service_type || '—'],
      ['Property Type', order.sr_property_type || order.property_name || '—'],
      ['Postcode', order.sr_postcode || order.postcode || order.location || '—'],
      ['Contact', order.contact_name || '—'],
      ['Preferred Date', order.preferred_service_date ? new Date(order.preferred_service_date).toLocaleDateString('en-GB') : (order.quoted_date ? new Date(order.quoted_date).toLocaleDateString('en-GB') : '—')],
    ];
    details.forEach(([label, value], i) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(label + ':', 14, 70 + i * 7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text(value, 60, 70 + i * 7);
    });

    // Amount box
    const boxY = 110;
    doc.setFillColor(240, 253, 244);
    doc.setDrawColor(...green);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, boxY, 182, 24, 3, 3, 'FD');
    doc.setTextColor(...green);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Quoted Value', 20, boxY + 9);
    doc.setFontSize(16);
    doc.text(`£${amountGBP}`, 196, boxY + 9, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('All prices include VAT where applicable', 20, boxY + 18);

    // Footer
    doc.setFillColor(...green);
    doc.rect(0, 278, 210, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text('Virewire Property Services  |  virewire.com', 105, 289, { align: 'center' });

    doc.save(`virewire-quotation-${order.id}.pdf`);
  };

  const handleOpenDocument = (url?: string | null) => {
    if (!url) {
      toast({ title: 'Document unavailable', description: 'This document does not have a file attached yet.', variant: 'destructive' });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCancelOrder = async () => {
    if (!order || !id) return;
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      const response = await apiService.request(`/bookings/${id}/cancel`, 'POST');
      if (response.success) {
        toast({ title: 'Order cancelled', description: 'Your order has been cancelled.' });
        navigate('/active-orders');
      } else {
        toast({ title: 'Unable to cancel order', description: response.error || 'Please contact support.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Unable to cancel order', description: 'Please contact support to cancel this order.', variant: 'destructive' });
    }
  };

  // Calculate invoice data from actual order
  const totalAmountPence = order?.total_amount_pence ?? 0;
  const isPaid = order?.is_paid === 1 || order?.payment_status === 'paid';
  const isCancelled = order?.status === 'cancelled' || order?.payment_status === 'cancelled';

  const subtotalPence = totalAmountPence / 1.20;
  const subtotal = Math.round(subtotalPence) / 100;
  const vat = (totalAmountPence - Math.round(subtotalPence)) / 100;
  const totalPrice = totalAmountPence / 100;
  
  const tabs = [
    'Overview',
    'Service Details',
    'Timeline',
  ];

  const statusMessage = (() => {
    const s = (order?.booking_status || order?.status || '').toLowerCase();
    if (s === 'completed') return 'This service has been completed. Thank you for using Virewire.';
    if (s === 'cancelled') return 'This order has been cancelled. Contact support if you need assistance.';
    if (s === 'in_progress' || s === 'in progress') return 'Our team is currently on-site. We are monitoring the progress for you.';
    if (s === 'confirmed') return 'Your order is confirmed. Our team will be in touch to arrange access.';
    if (s === 'pending') return 'Your order is being reviewed. We will provide an update shortly.';
    return 'Your order is being processed. Our team will be in touch.';
  })();

  const nextStepMessage = (() => {
    const s = (order?.booking_status || order?.status || '').toLowerCase();
    if (s === 'completed') return 'No further action required. You can download your invoice from the Invoice & Payment tab.';
    if (s === 'cancelled') return 'This order is closed. Please contact support if you have any queries.';
    if (s === 'in_progress' || s === 'in progress') return 'Service is in progress. We will notify you once the job is complete.';
    if (s === 'confirmed') return 'Your appointment is scheduled. Ensure access to the property is available on the service date.';
    return 'We have finished our preliminary assessment. Your quote is ready. Approve now to proceed to payment and scheduling your service visit.';
  })();

  const serviceDetails = {
    issueReported: order?.description || 'Service requested',
    requestedTimeline: order?.preferred_service_date || 'To be confirmed',
    pricingModel: 'Fixed Quote',
    protection: 'Virewire Guarantee Included',
    accessMethod: order?.accessInstructions || 'To be confirmed',
    parking: order?.parking || 'To be confirmed',
  };

  // Generate invoice data from actual order
  const dueDate = order?.updated_at 
    ? new Date(order.updated_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
  
  const paymentDate = order?.payment_completed_at
    ? new Date(order.payment_completed_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
    : dueDate;

  const invoiceData = {
    status: isPaid ? 'Paid' : 'Payment Pending',
    dueDate: dueDate,
    lineItems: [
      {
        name: order?.service_name || 'Service',
        description: order?.description || 'Professional service',
        cost: subtotal,
        qty: 1,
      },
    ],
    subtotal: subtotal,
    vat: vat,
    adjustments: 0,
    total: totalPrice,
    amountPaid: isPaid ? -totalPrice : 0,
    paymentHistory: isPaid ? [
      {
        type: 'Full Payment Received',
        amount: -totalPrice,
        date: paymentDate,
        method: 'Card payment',
      },
    ] : [
      {
        type: 'Invoice Issued',
        amount: 0,
        date: dueDate,
        method: 'Awaiting payment',
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{order?.service_name || 'Order'} | Virewire Property Services</title>
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate('/active-orders')} 
            className="mb-4 h-8 px-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Active Orders
          </Button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  {orderNumber}
                </p>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  {order.service_name}
                </h1>
                <div className="flex items-center gap-1 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>{order.property_name || order.postcode || 'Property location not specified'}</span>
                </div>
              </div>
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700 mb-4">
                  <span className="w-2 h-2 bg-blue-700 rounded-full mr-2"></span>
                  {order.booking_status || order.status || 'Pending'}
                </span>
                <p className="text-4xl font-bold text-gray-900">
                  £{amountGBP}
                </p>
              </div>
            </div>

            {/* Status Box */}
            <Card className="border-1 border-green-200 bg-green-50 mb-6">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-700 flex-shrink-0" />
                <p className="text-sm text-green-900 font-medium">
                  {statusMessage}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <div className="flex gap-8 overflow-x-auto">
              {tabs.map((tab) => {
                const normalizedTab = normalizeTabName(tab);
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(normalizedTab)}
                    className={`py-4 px-2 font-semibold border-b-2 whitespace-nowrap transition-colors ${
                      activeTab === normalizedTab
                        ? 'text-gray-900 border-gray-900'
                        : 'text-gray-600 border-transparent hover:text-gray-900'
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-6">
                {/* Next Expected Step */}
                <Card className="border-1 border-green-200 bg-green-50">
                  <CardContent className="p-6 flex items-start gap-4">
                    <AlertCircle className="h-6 w-6 text-green-700 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">
                        Next Expected Step
                      </h3>
                      <p className="text-sm text-gray-700">
                        {nextStepMessage}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Service Summary */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Service Summary
                    </h3>
                    <button
                      onClick={() => setActiveTab('service-details')}
                      className="text-[#00543b] text-sm font-semibold hover:underline"
                    >
                      View full details →
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex gap-4">
                      <MessageSquare className="h-6 w-6 text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                          Issue Reported
                        </p>
                        <p className="font-semibold text-gray-900">
                          {serviceDetails.issueReported}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Clock className="h-6 w-6 text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                          Requested Timeline
                        </p>
                        <p className="font-semibold text-gray-900">
                          {serviceDetails.requestedTimeline}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Property & Access */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Property & Access
                  </h3>
                  <Card className="border-gray-200">
                    <CardContent className="p-6 flex gap-6">
                      <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="grid grid-cols-2 gap-6 mb-4">
                          <div>
                            <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                              Access Method
                            </p>
                            <p className="font-semibold text-gray-900">
                              {serviceDetails.accessMethod}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                              Parking
                            </p>
                            <p className="font-semibold text-gray-900">
                              {serviceDetails.parking}
                            </p>
                          </div>
                        </div>
                        {order?.accessInstructions && (
                          <p className="text-sm text-gray-600">
                            Access notes: {order.accessInstructions}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Contact Support */}
                <Card className="border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => navigate('/dashboard/support')}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <MessageSquare className="h-6 w-6 text-gray-400" />
                      <div>
                        <h3 className="font-bold text-gray-900">
                          Need help with this order?
                        </h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Take Action */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Take Action</h3>
                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        className="w-full border-gray-300"
                        onClick={() => navigate('/dashboard/support')}
                      >
                        Request Reschedule
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-gray-300 text-red-600 hover:text-red-700"
                        onClick={() => navigate('/dashboard/support')}
                      >
                        Report an Issue
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full text-gray-900 hover:bg-gray-100"
                        onClick={handleCancelOrder}
                      >
                        Cancel Order
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Order Details */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-gray-600 uppercase mb-4">
                      Order Details
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-600">Order ID</p>
                        <p className="font-semibold text-gray-900">
                          {order?.id || orderNumber}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Created Date</p>
                        <p className="font-semibold text-gray-900">
                          {order?.created_at 
                            ? new Date(order.created_at).toLocaleDateString('en-GB', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric' 
                              })
                            : 'Date not available'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Property</p>
                        <button
                          onClick={() => navigate('/properties')}
                          className="font-semibold text-[#00543b] hover:underline"
                        >
                          {order?.property_name || order?.postcode || 'Property'}
                        </button>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Service Type</p>
                        <p className="font-semibold text-gray-900">
                          {order?.service_name || 'Service'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Assigned Provider</p>
                        <p className="font-semibold text-gray-900">
                          <span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span>
                          {order?.provider_name || 'Assigned Team'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Help Section */}
                <Card className="border-gray-200 bg-gray-50">
                  <CardContent className="p-6">
                    <p className="text-xs font-bold text-gray-600 uppercase mb-3">
                      Need help?
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      Our dedicated support managers are available to help with
                      any questions regarding this quote or the service journey.
                    </p>
                    <Button className="w-full bg-[#00543b] hover:bg-[#003d2d]" onClick={() => navigate('/dashboard/support')}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Message Virewire
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Service Details Tab */}
          {activeTab === 'service-details' && (
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2">
                {/* Scope of Work */}
                <Card className="border-gray-200 mb-6">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      <File className="h-6 w-6 text-[#00543b] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">
                          Service Details
                        </h3>
                        <p className="text-gray-700 mb-4">
                          {order?.description || 'Service details will be displayed here once available.'}
                        </p>

                        {/* Request Context */}
                        {order?.description && (
                          <div className="bg-blue-50 border-l-4 border-[#00543b] p-4 mb-4">
                            <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                              Request Details
                            </p>
                            <p className="text-sm text-gray-700 italic">
                              {order.service_name || 'Service'} - {order?.postcode || 'Location'}
                            </p>
                          </div>
                        )}

                        {/* Service Specifications */}
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">
                              Professional service execution
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">
                              Quality assurance included
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">
                              Virewire guarantee protection
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quoted Inclusions */}
                <Card className="border-gray-200 mb-6 bg-[#00543b]">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-4">
                      What's Included
                    </h3>
                    <ul className="text-white space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Professional service delivery
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Quality assurance checks
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Customer satisfaction guarantee
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Virewire support team available
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Exclusions */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Important Notes
                    </h3>
                    <ul className="text-gray-700 space-y-2">
                      <li className="text-sm">
                        • Service availability based on supplier schedule
                      </li>
                      <li className="text-sm">
                        • Additional charges may apply for extra requirements
                      </li>
                      <li className="text-sm">
                        • Access to property must be available on service date
                      </li>
                      <li className="text-sm">
                        • Full terms available in service agreement
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Financial Breakdown */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4">
                      Quote Summary
                    </h3>
                    <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                      <div className="flex justify-between">
                        <span className="text-gray-700">
                          Service Cost
                        </span>
                        <span className="font-bold text-gray-900">
                          £{subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">VAT (20%)</span>
                        <span className="font-bold text-gray-900">
                          £{vat.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="text-center mb-4">
                      <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                        Total Quoted Value
                      </p>
                      <p className="text-3xl font-bold text-gray-900">
                        £{amountGBP}
                      </p>
                    </div>
                    <Button className="w-full bg-[#00543b] hover:bg-[#003d2d]" onClick={handleDownloadPDF}>
                      <Download className="h-4 w-4 mr-2" />
                      View Full Quotation (PDF)
                    </Button>
                  </CardContent>
                </Card>

                {/* Project Team */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-gray-600 uppercase mb-4">
                      Service Provider
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-2"></div>
                        <p className="font-bold text-gray-900">{order?.provider_name || 'Assigned Provider'}</p>
                        <p className="text-xs text-gray-600">Service Partner</p>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full border-[#00543b] text-[#00543b]"
                        onClick={() => navigate('/dashboard/support')}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Contact Provider
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Need a change? */}
                <Card className="border-gray-200 bg-gray-900 text-white">
                  <CardContent className="p-6">
                    <h3 className="font-bold mb-2">Need a change?</h3>
                    <p className="text-sm text-gray-300 mb-4">
                      Request scope adjustments or add-on services directly
                      through our managed portal.
                    </p>
                    <Button className="w-full bg-white text-gray-900 hover:bg-gray-100" onClick={() => navigate('/dashboard/support')}>
                      REQUEST CHANGE ORDER
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-6">
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-gray-900">
                        Files & Assets
                      </h3>
                      <p className="text-sm text-gray-600">
                        {docsLoading ? 'Loading...' : `${documents.length} document${documents.length !== 1 ? 's' : ''}`}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {docsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                      ) : documents.length === 0 ? (
                        <div className="text-center py-8 text-gray-600">
                          <File className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                          <p>No documents available yet</p>
                        </div>
                      ) : (
                        documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <File className="h-8 w-8 text-green-600" />
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {doc.name}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {doc.size || 'Document'} • {doc.date}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold px-2 py-1 bg-white rounded text-gray-700">
                                {doc.type}
                              </span>
                              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => handleOpenDocument(doc.url)}>
                                <Download className="h-4 w-4 text-gray-600" />
                              </button>
                              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => handleOpenDocument(doc.url)}>
                                <Eye className="h-4 w-4 text-gray-600" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}

                      {!docsLoading && documents.length > 0 && order?.booking_status !== 'completed' && (
                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                          <Clock className="h-8 w-8 text-gray-400" />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              Completion Report Awaiting
                            </p>
                            <p className="text-sm text-gray-600">
                              We will generate this report once all safety checks
                              are finalized.
                            </p>
                          </div>
                          <span className="text-xs font-bold px-3 py-1 bg-yellow-100 text-yellow-700 rounded">
                            PROCESSING
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                {/* Add Supporting Files */}
                <Card className="border-gray-200 bg-[#00543b] text-white">
                  <CardContent className="p-6">
                    <h3 className="font-bold mb-2">Add Supporting Files</h3>
                    <p className="text-sm text-gray-200 mb-4">
                      Need to share specific site photos or compliance permits?
                      We can review them instantly.
                    </p>
                    <Button className="w-full bg-white text-[#00543b] hover:bg-gray-100" onClick={() => navigate('/dashboard/support')}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Additional Documents
                    </Button>
                  </CardContent>
                </Card>

                {/* Virewire Verification */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-gray-600 uppercase mb-3 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-[#00543b]" />
                      Virewire Verification
                    </h3>
                    <p className="text-sm text-gray-700 mb-3">
                      Our team validates all documentation against national
                      safety standards. We maintain a full audit trail for your
                      compliance records for 7 years.
                    </p>
                  </CardContent>
                </Card>

                {/* Assigned Provider */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-gray-600 uppercase mb-4">
                      Assigned Provider
                    </h3>
                    <p className="font-semibold text-gray-900">
                      {order?.provider_name || 'Not yet assigned'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="space-y-8">
              {photosLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Before */}
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 bg-gray-400 rounded-full"></span>
                        <h3 className="text-lg font-bold text-gray-900">Before</h3>
                        <span className="text-xs font-bold text-gray-600 bg-gray-200 px-2 py-1 rounded-full">
                          {photos.before.length} IMAGE{photos.before.length !== 1 ? 'S' : ''}
                        </span>
                      </div>
                    </div>
                    {photos.before.length === 0 ? (
                      <div className="flex items-center justify-center h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                        <div className="text-center">
                          <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600 text-sm">No before photos yet</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        {photos.before.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt="Before"
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* During */}
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
                        <h3 className="text-lg font-bold text-gray-900">During</h3>
                        <span className="text-xs font-bold text-white bg-green-500 px-2 py-1 rounded-full">
                          {photos.during.length} IMAGE{photos.during.length !== 1 ? 'S' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {photos.during.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img}
                            alt="During"
                            className="w-full h-40 object-cover rounded-lg"
                          />
                          {idx === 0 && photos.during.length > 0 && (
                            <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-white text-sm font-bold text-center">
                                CURRENT PROGRESS<br />Work in progress
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                      {photos.during.length === 0 && (
                        <div className="col-span-3 flex items-center justify-center h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                          <div className="text-center">
                            <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600 text-sm">
                              No during/progress photos yet
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Evidence */}
                  {photos.evidence.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <span className="inline-block w-3 h-3 bg-red-500 rounded-full"></span>
                        <h3 className="text-lg font-bold text-gray-900">Evidence</h3>
                        <span className="text-xs font-bold text-white bg-red-500 px-2 py-1 rounded-full">
                          {photos.evidence.length} ISSUE{photos.evidence.length !== 1 ? 'S' : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        {photos.evidence.map((item, idx) => (
                          <Card key={idx} className="border-gray-200 overflow-hidden">
                            <div className="relative h-40 bg-gray-200">
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-xs font-bold text-red-600 uppercase">
                                  {item.category}
                                </p>
                              </div>
                              <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-700">
                                {item.description}
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2">
                <div className="space-y-4">
                  {timelineLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : timeline.length === 0 ? (
                    <div className="text-center py-8 text-gray-600">
                      <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                      <p>No timeline events yet</p>
                    </div>
                  ) : (
                    timeline.map((event, idx) => (
                      <div key={event.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                            {event.icon === 'check' && <CheckCircle className="h-5 w-5" />}
                            {event.icon === 'vehicle' && '🚗'}
                            {event.icon === 'payment' && '✓'}
                          </div>
                          {idx < timeline.length - 1 && (
                            <div className="w-0.5 h-16 bg-blue-200 my-2"></div>
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex items-baseline gap-2 mb-1">
                            <p className="font-bold text-gray-900">{event.actor}</p>
                            <p className="font-bold text-gray-600">
                              — {event.action}
                            </p>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">
                            {event.description}
                          </p>
                          <p className="text-xs text-gray-600">{event.date}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {/* Order Actions */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4">
                      ORDER ACTIONS
                    </h3>
                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        className="w-full border-gray-300"
                        onClick={() => navigate('/dashboard/support')}
                      >
                        Request Reschedule
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-gray-300 text-red-600"
                        onClick={() => navigate('/dashboard/support')}
                      >
                        Report an Issue
                      </Button>
                      <button className="w-full text-[#00543b] text-sm font-semibold hover:underline" onClick={() => navigate('/dashboard/support')}>
                        Contact Support
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* Order Summary */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-gray-600 uppercase mb-4">
                      ORDER SUMMARY
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-600">Created Date</p>
                        <p className="font-bold text-gray-900">
                          {order?.created_at 
                            ? new Date(order.created_at).toLocaleDateString('en-GB', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric' 
                              })
                            : 'Date not available'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Property</p>
                        <p className="font-bold text-gray-900">{order?.property_name || order?.postcode || 'Property'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Service Type</p>
                        <p className="font-bold text-gray-900">
                          {order?.service_name || 'Service'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Assigned Team</p>
                        <p className="font-bold text-gray-900">
                          <span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span>
                          {order?.provider_name || 'Assigned Team'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Need assistance */}
                <Card className="border-gray-200 bg-[#00543b] text-white">
                  <CardContent className="p-6">
                    <h3 className="font-bold mb-2">Need assistance?</h3>
                    <p className="text-sm text-gray-200 mb-4">
                      Our dedicated support managers are available to help with
                      your current order.
                    </p>
                    <Button className="w-full bg-white text-[#00543b] hover:bg-gray-100" onClick={() => navigate('/dashboard/support')}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Message Virewire
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Invoice & Payment Tab */}
          {activeTab === 'invoice-payment' && (
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2">
                {/* Payment Status */}
                <Card className="border-gray-200 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className={`text-xs font-bold uppercase ${isPaid ? 'text-green-600' : isCancelled ? 'text-gray-500' : 'text-red-600'}`}>
                          Payment Status
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {totalAmountPence === 0 ? 'Pricing Pending' : isPaid ? 'Paid' : isCancelled ? 'Cancelled' : 'Payment Pending'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase">
                          {isPaid ? 'Paid On' : 'Due Date'}
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {isPaid ? paymentDate : invoiceData.dueDate}
                        </p>
                      </div>
                    </div>
                    {totalAmountPence === 0 && (
                      <Card className="border-yellow-300 bg-yellow-50 p-4 mb-4">
                        <CardContent className="p-0">
                          <p className="text-sm text-yellow-800">
                            ⚠️ Pricing information is being finalized. Contact support if this takes longer than expected.
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>

                {/* Line Items */}
                <Card className="border-gray-200 mb-6">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Line Items</h3>
                    <div className="space-y-3">
                      {invoiceData.lineItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-start gap-4">
                            <Shield className="h-6 w-6 text-gray-400 flex-shrink-0 mt-1" />
                            <div>
                              <p className="font-semibold text-gray-900">
                                {item.name}
                              </p>
                              <p className="text-sm text-gray-600">
                                {item.description}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">
                              £{item.cost.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-600">
                              QTY {item.qty}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Payment History */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-4">
                      Payment History
                    </h3>
                    <div className="space-y-3">
                      {invoiceData.paymentHistory.map((payment, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <CheckCircle
                              className={`h-5 w-5 ${
                                payment.amount < 0
                                  ? 'text-green-500'
                                  : 'text-gray-400'
                              }`}
                            />
                            <div>
                              <p className="font-semibold text-gray-900">
                                {payment.type}
                              </p>
                              <p className="text-sm text-gray-600">
                                {payment.method}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={`font-bold ${
                                payment.amount < 0
                                  ? 'text-green-600'
                                  : 'text-gray-900'
                              }`}
                            >
                              {payment.amount < 0
                                ? '−'
                                : '+'}
                              £{Math.abs(payment.amount).toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-600">
                              {payment.date}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Invoice Summary */}
                <Card className="border-gray-200 bg-[#00543b] text-white">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-bold text-yellow-100 uppercase mb-4">
                      Invoice Summary
                    </h3>
                    {totalAmountPence === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-yellow-100 mb-4">
                          💷 Pricing information is being calculated
                        </p>
                        <p className="text-xs text-yellow-200 mb-6">
                          We're finalizing your quote details. Please check back shortly.
                        </p>
                        <Button className="w-full bg-white/20 text-white hover:bg-white/30" disabled>
                          PAY NOW - Pending
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 mb-6 pb-6 border-b border-green-700">
                          <div className="flex justify-between">
                            <span>Subtotal</span>
                            <span className="font-bold">
                              £{invoiceData.subtotal.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>VAT (20%)</span>
                            <span className="font-bold">
                              £{invoiceData.vat.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Adjustments</span>
                            <span className="font-bold">
                              £{invoiceData.adjustments.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="text-center mb-6">
                          <p className="text-xs text-yellow-100 uppercase mb-1">
                            {isPaid ? 'Total Paid' : 'Total Due'}
                          </p>
                          <p className="text-3xl font-bold">
                            £{invoiceData.total.toFixed(2)}
                          </p>
                        </div>
                        {isPaid ? (
                          <div className="w-full bg-green-500/30 text-white rounded-lg py-3 text-center font-bold flex items-center justify-center gap-2">
                            <CheckCircle className="h-5 w-5" />
                            PAYMENT RECEIVED
                          </div>
                        ) : isCancelled ? (
                          <div className="w-full bg-white/10 text-gray-300 rounded-lg py-3 text-center font-bold">
                            ORDER CANCELLED
                          </div>
                        ) : (
                          <>
                            <Button className="w-full bg-white text-[#00543b] hover:bg-gray-100 font-bold" onClick={() => navigate(`/service-requests/${order?.quote_id || order?.booking_id}/payment`)}>
                              PAY NOW - £{invoiceData.total.toFixed(2)}
                            </Button>
                            <p className="text-xs text-gray-300 text-center mt-2">
                              Secure payment powered by Stripe
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Billing Support */}
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2">
                      Billing Support
                    </h3>
                    <p className="text-xs text-gray-600 uppercase font-bold mb-3">
                      Available 24/7
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      Our finance team is here to assist with any billing
                      discrepancies or payment arrangements.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-gray-300"
                      onClick={() => navigate('/dashboard/support')}
                    >
                      Contact Finance Team
                    </Button>
                  </CardContent>
                </Card>

                {/* Property Asset */}
                <Card className="border-gray-200 bg-gradient-to-br from-green-900 to-green-950 text-white">
                  <CardContent className="p-6">
                    <p className="text-xs font-bold text-green-200 uppercase mb-2">
                      Property Asset
                    </p>
                    <p className="text-lg font-bold">{order?.property_name || order?.postcode || 'Property'}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default OrderDetailPage;
